
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCandidateProfileInDbInternal } from "@/services/candidateService";
import type { Candidate, UpdateCandidateData } from "@/components/candidates/types";
import { trackCandidateUpdated } from '@/utils/analytics';
import { toast } from 'sonner';

export function useUpdateCandidateProfile() {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    Candidate | null,
    Error,
    { candidateId: string; data: UpdateCandidateData }
  >({
    mutationFn: ({ candidateId, data }) => 
      updateCandidateProfileInDbInternal(candidateId, data),
    onSuccess: (updatedCandidate, variables) => {
      if (updatedCandidate) {
        queryClient.invalidateQueries({ queryKey: ['candidates'] });
        queryClient.invalidateQueries({ queryKey: ['candidate', updatedCandidate.id] }); // Invalidate specific candidate query
        
        const candidateName = `${updatedCandidate.first_name || ''} ${updatedCandidate.last_name || ''}`.trim();
        toast.success(`${candidateName || 'Candidate'}'s profile updated successfully!`);
        
        // Track GA event
        // Get updated fields from variables.data keys
        const updatedFields = Object.keys(variables.data);
        trackCandidateUpdated(updatedCandidate.id, updatedFields, candidateName);
      } else {
        // This case might occur if the DB update returns null (e.g. candidate not found)
        toast.info("Profile update may not have completed. Candidate data not returned."); // Changed from toast.warn to toast.info
      }
    },
    onError: (error) => {
      toast.error(`Failed to update candidate profile: ${error.message}`);
      console.error("Mutation error in useUpdateCandidateProfile:", error);
    },
  });

  return {
    updateCandidateProfile: mutate,
    isUpdatingProfile: isPending,
  };
}

